// The free month, in the masthead: the gold Subscribe button beside the
// sign-in (SignedInBadge) and the strip under the masthead (FreeMonthStrip).
// Owner, 2026-11-03: "I don't see where someone can subscribe anytime" —
// until then the only way to buy during the free month was Account, then
// scroll. Mock https://claude.ai/artifact/3fBoVMTbhQsywYnRBth49d, approved.
//
// ⚠️ LOADED LATE, LIKE SignedInBadge. TickerChrome is in the PRERENDERED
// homepage, built under Node, where the Supabase client throws on import. The
// client is imported dynamically inside an effect, so the server's HTML and the
// browser's first render both draw nothing, and hydration agrees.
//
// ⚠️ ONE READ PER PAGE LOAD, shared by both pieces (a module-level promise).
// The level comes from rb_site_my_billing — the database's rb_site_level, the
// same answer the gate and the account page use — so the header can never say
// "21 days free" to someone who has paid or been given a plan.
//
// ⚠️ RequireAccount draws its own strip for pages without this masthead (the
// app's screens). It must not draw a second one under ours, so this strip
// registers itself with FreeMonthStripSlot (useLayoutEffect: before paint).

import { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";

import { daysLeft, freeMonthStrip } from "@/lib/siteAccess";
import { money, SITE_PLAN } from "@/lib/sitePlan";

export type FreeMonth = { left: number; endsAt: string } | null;

let pending: Promise<FreeMonth> | null = null;

function loadFreeMonth(): Promise<FreeMonth> {
  if (!pending) {
    pending = import("@/integrations/supabase/client")
      .then(async ({ supabase }) => {
        const { data: s } = await supabase.auth.getSession();
        if (!s.session) return null;
        const { data, error } = await supabase.rpc("rb_site_my_billing" as never);
        if (error || !data) return null;
        const b = data as { level?: string | null; trial_ends_at?: string | null };
        if (b.level !== "trial" || !b.trial_ends_at) return null;
        const left = daysLeft(b.trial_ends_at);
        return left && left > 0 ? { left, endsAt: b.trial_ends_at } : null;
      })
      // No session module, no billing read: say nothing. Never break the masthead over this.
      .catch(() => null);
  }
  return pending;
}

export function useFreeMonth(): FreeMonth {
  const [v, setV] = useState<FreeMonth>(null);
  useEffect(() => {
    let live = true;
    void loadFreeMonth().then((x) => { if (live) setV(x); });
    return () => { live = false; };
  }, []);
  return v;
}

/** Where the Subscribe links go: the purchase page (SubscribePage, 2026-11-04). */
export const SUBSCRIBE_HREF = "/subscribe";

/** RequireAccount provides this; the masthead's strip tells it "I'm here". */
export const FreeMonthStripSlot = createContext<((here: boolean) => void) | null>(null);

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export function FreeMonthStrip() {
  const fm = useFreeMonth();
  const claim = useContext(FreeMonthStripSlot);
  useIsoLayoutEffect(() => {
    if (!claim) return;
    claim(true);
    return () => claim(false);
  }, [claim]);
  if (!fm) return null;
  const s = freeMonthStrip(fm.left, fm.endsAt, money(SITE_PLAN.monthly));
  return (
    <p className="rbtx-freestrip" data-tone={s.tone}>
      {s.text} · <a href={SUBSCRIBE_HREF}>{s.cta}</a>
    </p>
  );
}
